import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays, Calendar, Tag, Users, Waves } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-cabanas.jpg";
import cabanaImg from "@/assets/cabanas/cabana-riverside.jpg";
import tubingImg from "@/assets/tubing/family-tubing-fun.jpg";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";

const PUBLISH_DATE = "May 9, 2026";
const CANONICAL = "/blog/memorial-day-weekend-guadalupe-river";
const TITLE = "Memorial Day Weekend on the Guadalupe River — 2026 Guide | Son's";
const DESCRIPTION =
  "Plan the perfect Memorial Day weekend on the Guadalupe River — what's open at Son's, booking deadlines, pricing, kid tips, and how to skip the crowds.";

const FAQS = [
  {
    q: "When should I book Memorial Day weekend on the Guadalupe?",
    a: "Memorial Day weekend is the busiest weekend of the early summer. We recommend booking your cabana, glamping tent, or campsite at least 4–6 weeks in advance. Same-week availability is rare.",
  },
  {
    q: "Is the Guadalupe River open for tubing on Memorial Day?",
    a: "Yes — Son's Guadalupe is open for tubing, cabana rentals, and overnight stays Memorial Day weekend 2026. River conditions are typically excellent in late May.",
  },
  {
    q: "Are kids welcome Memorial Day weekend?",
    a: "Absolutely. Son's is family-friendly year-round and Memorial Day weekend is perfect for kids — warm water, calm pools, and plenty of room at your private cabana.",
  },
  {
    q: "How can I avoid the crowds on Memorial Day weekend?",
    a: "Book a private cabana so you have your own shaded basecamp instead of fighting for parking. Arrive when we open at 10:30 AM and float earlier in the day. Glamping or overnight stays let you enjoy the river before and after the day-trip rush.",
  },
];

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "Memorial Day Weekend on the Guadalupe River — 2026 Cabanas & Tubing Guide",
    description: DESCRIPTION,
    image: "https://sonsguadalupe.com/og-image.png",
    author: { "@type": "Organization", name: "Son's Guadalupe" },
    datePublished: "2026-05-09",
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
    keywords:
      "memorial day weekend new braunfels, memorial day tubing guadalupe, memorial day cabana rental, things to do memorial day texas",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
      { "@type": "ListItem", position: 3, name: "Memorial Day Weekend on the Guadalupe River", item: `https://sonsguadalupe.com${CANONICAL}` },
    ],
  },
];

const BlogMemorialDay2026 = () => {
  const related = getRelatedPosts("memorial-day-weekend-guadalupe-river", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} ogImage="/og/blog-memorial-day.jpg" schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Memorial Day weekend cabanas on the Guadalupe River near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Memorial Day Weekend on the Guadalupe River — 2026 Cabanas & Tubing Guide
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                <strong>Memorial Day weekend</strong> is the unofficial start of summer in Texas — and there's no better place to celebrate it than the Guadalupe River. Here's everything you need to plan a stress-free Memorial Day at Son's: what's open, what to book, and how to skip the worst of the crowds.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Calendar className="w-5 h-5 text-teal" /> Memorial Day 2026 Dates</h2>
              <p>
                Memorial Day 2026 falls on <strong>Monday, May 25</strong>. Most guests come Friday May 22 through Monday May 25 for a 3- or 4-night weekend. River weather in late May is reliably warm (mid-80s to 90s), and the Guadalupe is running at its prettiest spring-fed clarity.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Waves className="w-5 h-5 text-teal" /> What's Open at Son's Memorial Day Weekend</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cabana rentals</strong> — all 22 cabanas open for day rentals from 10:30 AM to 6:30 PM</li>
                <li><strong>In-water picnic tables</strong> — riverside tables right in the shallow water</li>
                <li><strong>Tubing</strong> — heavy-duty Son's tubes, $24.99 per person</li>
                <li><strong>Glamping</strong> — bell tents with AC, queen beds, fire pits, available all weekend</li>
                <li><strong>Tent camping</strong> — riverside sites at Son's River Ranch and Blue River Camp</li>
              </ul>

              <img src={cabanaImg} alt="Memorial Day weekend cabana setup on the Guadalupe River" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Tag className="w-5 h-5 text-teal" /> Pricing & Booking Deadlines</h2>
              <p>
                Cabana rentals start at <strong>$69</strong> on weekdays and run higher Memorial Day weekend due to demand. Glamping starts at <strong>$129/night</strong> and tent camping at <strong>$45/night</strong>. The weekend regularly sells out 3–4 weeks in advance — if you're reading this in early May and you haven't booked, do it today.
              </p>
              <p>
                A 50% deposit holds your reservation. The remaining balance is due 7 days before arrival. Cancellation policy: more than 15 days out gets a 90% refund, 7–14 days gets 50%, and inside 7 days is non-refundable.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Users className="w-5 h-5 text-teal" /> Kid-Friendly Memorial Day Tips</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Book a cabana — your private shaded basecamp makes the day infinitely easier with kids</li>
                <li>Arrive at 10:30 AM open. Mid-day parking is the worst of the holiday crunch.</li>
                <li>Bring a kids' life jacket (we have rentals if you forget). Required for non-swimmers.</li>
                <li>Pack lunch and grill at the cabana — way cheaper than leaving for food</li>
                <li>Reserve a glamping tent if the kids want to stay overnight without sleeping on the ground</li>
              </ul>

              <img src={tubingImg} alt="Family tubing the Guadalupe River on Memorial Day weekend" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How to Beat the Crowds</h2>
              <p>
                The single biggest hack: book a private cabana or stay overnight. Day-trippers without reservations spend Memorial Day weekend hunting for parking and standing in line at outfitters. Son's guests check in, walk to their cabana, and have river access all day. If you're staying overnight, you'll have the river practically to yourself before 10:30 AM and after 6:30 PM.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Memorial Day FAQ</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book Memorial Day Weekend</Button></Link>
                <Link to="/cabanas"><Button variant="outline" size="lg" className="text-lg px-10 py-6">See Cabanas</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />
      </main>
      <SEOContentBlock
        eyebrow="Plan Your Memorial Day"
        heading="Cabanas, Tubing & Glamping for Memorial Day Weekend"
        paragraphs={[
          "Memorial Day weekend on the Guadalupe River books up fast. Lock in a cabana rental on the Guadalupe River, glamping near New Braunfels, or tent camping on River Road before the calendar fills.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Tent Camping", to: "/tent-camping-new-braunfels" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogMemorialDay2026;
